import React, { useState, useMemo } from 'react';
import type { ParsedXtreamChannel } from '@iptv/shared';
import '../ChannelSelector.css';

interface Props {
  channels: ParsedXtreamChannel[];
  onSubmit: (selectedChannels: ParsedXtreamChannel[]) => Promise<void>;
  onBack: () => void;
}

export function ChannelSelector({ channels, onSubmit, onBack }: Props) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'category'>('name');
  const [sortAsc, setSortAsc] = useState(true);

  const getChannelKey = (ch: ParsedXtreamChannel) => ch.xtream_stream_id || ch.name;

  // Sort channels
  const sortedChannels = useMemo(() => {
    const sorted = [...channels];
    sorted.sort((a, b) => {
      let aVal: string;
      let bVal: string;

      if (sortBy === 'name') {
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
      } else {
        aVal = (a.category || 'Uncategorized').toLowerCase();
        bVal = (b.category || 'Uncategorized').toLowerCase();
      }

      const cmp = aVal.localeCompare(bVal);
      return sortAsc ? cmp : -cmp;
    });
    return sorted;
  }, [channels, sortBy, sortAsc]);

  const toggleChannel = (ch: ParsedXtreamChannel) => {
    const key = getChannelKey(ch);
    const newSet = new Set(selectedIds);
    newSet.has(key) ? newSet.delete(key) : newSet.add(key);
    setSelectedIds(newSet);
  };

  const toggleAllChannels = () => {
    if (selectedIds.size === channels.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(channels.map(getChannelKey)));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const selected = channels.filter((ch) => selectedIds.has(getChannelKey(ch)));
      await onSubmit(selected);
    } finally {
      setLoading(false);
    }
  };

  const selectedCount = selectedIds.size;
  const totalCount = channels.length;

  return (
    <div className="channel-selector">
      <h2>Select Channels ({selectedCount}/{totalCount})</h2>

      <table className="channels-table">
        <thead>
          <tr className="table-header-row">
            <th className="table-header select-col">
              <input
                type="checkbox"
                checked={selectedCount === totalCount && totalCount > 0}
                onChange={toggleAllChannels}
              />
            </th>
            <th className="table-header logo-col"></th>
            <th
              className={`table-header sortable ${sortBy === 'name' ? 'active' : ''}`}
              onClick={() => {
                if (sortBy === 'name') {
                  setSortAsc(!sortAsc);
                } else {
                  setSortBy('name');
                  setSortAsc(true);
                }
              }}
            >
              Channel {sortBy === 'name' && (sortAsc ? '▲' : '▼')}
            </th>
            <th
              className={`table-header sortable ${sortBy === 'category' ? 'active' : ''}`}
              onClick={() => {
                if (sortBy === 'category') {
                  setSortAsc(!sortAsc);
                } else {
                  setSortBy('category');
                  setSortAsc(true);
                }
              }}
            >
              Category {sortBy === 'category' && (sortAsc ? '▲' : '▼')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedChannels.map((ch) => {
            const key = getChannelKey(ch);
            const isChecked = selectedIds.has(key);
            return (
              <tr
                key={key}
                className="table-data-row"
                onClick={() => toggleChannel(ch)}
              >
                <td className="table-cell select-col">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => {}}
                  />
                </td>
                <td className="table-cell logo-cell">
                  {ch.logo_url && (
                    <img src={ch.logo_url} alt="" className="table-logo" />
                  )}
                </td>
                <td className="table-cell name-cell">{ch.name}</td>
                <td className="table-cell category-cell">
                  {ch.category || 'Uncategorized'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="controls">
        <button onClick={onBack} disabled={loading}>
          Back
        </button>
        <button onClick={handleSubmit} disabled={loading || selectedCount === 0}>
          {loading ? 'Importing...' : `Import ${selectedCount} Channels`}
        </button>
      </div>
    </div>
  );
}
